import type { Permission } from '@mt/shared';
import {
  Ship,
  CalendarClock,
  Handshake,
  FileSearch,
  Hash,
  Coins,
  Anchor,
  ChartColumn,
  BellRing,
  Globe,
  BookImage,
  Sparkles,
  FerrisWheel,
  Stamp,
  Signature,
  KeyRound,
  Workflow,
  TrendingDown,
  ListChecks,
  Hammer,
  CircleDot,
  HardHat,
  HandCoins,
  Activity,
  AudioLines,
  AlarmClock,
  ClipboardCheck,
  PackagePlus,
  PackageX,
  ArrowRightLeft,
  History,
  MoonStar,
  BarChart3,
  BookOpen,
  BookText,
  Bot,
  ClipboardList,
  Clock,
  Calculator,
  FileSignature,
  FileCheck2,
  CalendarDays,
  FileInput,
  FilePlus2,
  ReceiptText,
  FileText,
  Home,
  IdCard,
  ListTree,
  RefreshCw,
  Route,
  Images,
  ImagePlay,
  UserRoundPen,
  FileAudio,
  ListTodo,
  FolderKanban,
  ShieldHalf,
  type LucideIcon,
  Package,
  PackageCheck,
  PackageOpen,
  Receipt,
  Settings,
  ShoppingCart,
  Tags,
  Truck,
  UserCog,
  Users,
  Warehouse,
  Wallet,
  Zap,
  Columns3,
  Building2,
  MapPinned,
  MapPinCheck,
  Link2,
  ScrollText,
  Upload,
  UserPlus,
  MessageSquareWarning,
  Target,
  TriangleAlert,
  TrendingUp,
  Gavel,
  Radar,
  BriefcaseBusiness,
  Library,
  Megaphone,
  ShieldCheck,
  CarFront,
  Gauge,
  Wrench,
  Fuel,
  Scale,
  Droplets,
  Map as MapIcon,
  Users2,
  UserRound,
  BadgeCheck,
  ShoppingBag,
  Layers,
  MapPin,
  GraduationCap,
  Newspaper,
  LibraryBig,
  BookOpenCheck,
  ShieldAlert,
  BookMarked,
  Landmark,
  Siren,
} from 'lucide-react';

export interface NavLeaf {
  to: string;
  label: string;
  icon: LucideIcon;
  perm?: Permission;
  /** Hiện khi có ít nhất 1 quyền trong danh sách (dùng thay `perm`). */
  anyPerm?: Permission[];
  /** Chức năng chưa có dữ liệu – mở trang "đang phát triển". */
  soon?: boolean;
  /** Chỉ cho nhân viên công ty: ẩn với tài khoản chỉ là CTV (DEC-268). */
  staffOnly?: boolean;
  /** Chỉ cho tài khoản chỉ là CTV (DEC-375): đơn của tôi, doanh số & hoa hồng. */
  ctvOnly?: boolean;
  /** Chỉ sáng khi đúng đường dẫn (không sáng cho trang con). */
  end?: boolean;
  badge?: 'followups';
  /** Mẫu đường dẫn sáng thêm (VD: /bao-gia/123 sáng mục Danh sách báo giá). */
  match?: RegExp;
  /** Tiêu đề phần con trong nhóm (VD: Vận tải → "Điều phối", "Đội xe"); hiện trước mục đầu tiên của phần. */
  section?: string;
  /** Anh Tiến 20/10/2026: "mục không có dữ liệu ẩn luôn" – danh sách rỗng trong phạm vi người xem thì ẩn mục (xem navData.ts). */
  hideIfEmpty?: NavDataKey;
}

export type NavDataKey = 'so3' | 'sales_orders' | 'contracts' | 'reservations';

export interface NavGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  children: NavLeaf[];
  tag?: string;
}

export type NavEntry = NavLeaf | NavGroup;
export const isGroup = (e: NavEntry): e is NavGroup => 'children' in e;

/**
 * Menu trái gom theo nhóm (anh Tiến 09/10/2026). Mục không có quyền bị ẩn; nhóm không còn mục nào cũng ẩn (AppShell).
 * Mục chưa có dữ liệu đánh dấu soon. Mục trỏ tới tab (`/doi-xe?tab=…`) chỉ sáng khi đúng tab.
 */
export const NAV: NavEntry[] = [
  {
    key: 'tong-quan',
    label: 'Tổng quan',
    icon: Home,
    children: [
      // Bảng điều khiển chỉ có số liệu bán hàng: người không xem được khách / báo giá không thấy mục này, mở app vào màn ô lớn (anh Tiến 14/10/2026)
      { to: '/tong-quan', label: 'Bảng điều khiển', icon: Gauge, end: true, anyPerm: ['quote.read', 'customer.read'] },
      // DEC-323 (anh Tiến 14/10/2026 "Giao việc nằm ở Tổng quan"): TP / GĐ / HR giao việc, lịch hẹn, xem báo cáo cuối ngày
      { to: '/giao-viec', label: 'Giao việc', icon: ClipboardList, anyPerm: ['hr.leave.approve', 'hr.employee.manage'] },
      // Ai cũng có: việc được giao, trao đổi trên việc, báo cáo cuối ngày
      { to: '/viec-cua-toi', label: 'Việc của tôi', icon: ListTodo, staffOnly: true },
      // P23: Hộp duyệt dùng chung – ai cũng có (phiếu chờ mình duyệt, phiếu mình gửi, tờ trình)
      { to: '/duyet', label: 'Hộp duyệt', icon: Stamp, staffOnly: true },
      // P23 PR 2: ký điện tử – ai cũng có (trình ký PDF, hồ sơ chờ mình ký)
      { to: '/ky-dien-tu', label: 'Ký điện tử', icon: Signature, staffOnly: true },
    ],
  },
  // CTV cũng dùng Bảng tin (anh Tiến 19/10/2026 "Vẫn hiển thị bảng tin", DEC-375)
  { to: '/bang-tin', label: 'Bảng tin', icon: Newspaper, perm: 'feed.use', end: true },
  // Anh Tiến 24/10/2026: vòng quay may mắn nằm chung mục Bảng tin (tab trong trang Bảng tin)
  { to: '/bang-tin/vong-quay', label: 'Vòng quay may mắn', icon: FerrisWheel, perm: 'feed.use' },
  {
    key: 'ban-hang',
    label: 'Bán hàng',
    icon: ShoppingCart,
    children: [
      { to: '/bao-gia/moi', label: 'Tạo báo giá', icon: FilePlus2, perm: 'quote.create', end: true },
      { to: '/bao-gia-nhanh', label: 'Báo giá nhanh', icon: Zap, perm: 'quote.create' },
      // DEC-521: khách gửi Báo giá nhanh trên web PCCC (file + thông tin) → NV nhận, lập báo giá
      { to: '/yeu-cau-bao-gia-web', label: 'Yêu cầu báo giá web', icon: Globe, perm: 'lead.read' },
      { to: '/bao-gia', label: 'Danh sách báo giá', icon: FileText, perm: 'quote.read', end: true, match: /^\/bao-gia\/\d+$/ },
      { to: '/duyet-gia', label: 'Chờ tôi duyệt', icon: ShieldCheck, perm: 'quote.approve' },
      // DEC-375: CTV xem đơn của mình (thanh 4 bước giao hàng), doanh số & hoa hồng tháng; NV quản lý CTV duyệt % CTV đề xuất
      { to: '/don-cua-toi', label: 'Đơn hàng của tôi', icon: PackageCheck, perm: 'quote.read', ctvOnly: true },
      { to: '/thu-nhap', label: 'Doanh số & hoa hồng', icon: HandCoins, perm: 'quote.read', ctvOnly: true },
      { to: '/duyet-hoa-hong-ctv', label: 'Duyệt % hoa hồng CTV', icon: HandCoins, perm: 'quote.read', staffOnly: true },
      // DEC-457: báo giá dự toán BOQ PCCC / HVAC / ME – menu riêng, không lẫn Danh sách báo giá; vẫn có thẻ trên Pipeline
      { to: '/bao-gia-du-toan', label: 'Báo giá dự toán', icon: Calculator, perm: 'estimate.read', match: /^\/bao-gia-du-toan\/\d+$/ },
      { to: '/doi-chieu-cong-no', label: 'Đối chiếu công nợ', icon: Scale, perm: 'customer.read_finance', match: /^\/doi-chieu-cong-no\/\d+$/ },
      // Anh Tiến 15/10/2026: gom Đơn hàng + Hợp đồng thành "Phiếu xuất bán hàng – hợp đồng" giống Asia.
      // Chốt đơn = Kiểm soát đơn chốt SO3 (DEC-273); Đơn hàng Asia = phiếu SO3 Asia (chỉ xem); Đơn hàng ERP, Hợp đồng, Báo giá đã chốt là mục riêng
      // Anh Tiến 19/10/2026: Chốt đơn chỉ còn soạn hàng (SO3, hàng thiếu) + điều phối giao; Kiểm soát đơn chốt, Báo giá đã chốt, Soát phiếu có mục riêng
      {
        section: 'Phiếu xuất bán hàng – Hợp đồng',
        to: '/chot-don',
        label: 'Soạn Hàng – Điều Phối',
        icon: ClipboardCheck,
        anyPerm: ['sales_order.read', 'control.check', 'purchase.read', 'dispatch.plan', 'delivery.read_all'],
        end: true,
      },
      // Anh Tiến 19/10/2026: "Đơn hàng Asia" = trạng thái kiểm tra các đơn SO3 trên Asia; bỏ thanh tab, mỗi màn một mục riêng
      { section: 'Phiếu xuất bán hàng – Hợp đồng', to: '/phieu-xuat', label: 'Đơn hàng Asia', icon: FileSignature, perm: 'sales_order.read', hideIfEmpty: 'so3' },
      { section: 'Phiếu xuất bán hàng – Hợp đồng', to: '/don-hang', label: 'Đơn hàng ERP', icon: ShoppingBag, perm: 'sales_order.read', match: /^\/don-hang\/\d+$/, hideIfEmpty: 'sales_orders' },
      { section: 'Phiếu xuất bán hàng – Hợp đồng', to: '/hop-dong', label: 'Hợp đồng', icon: FileText, perm: 'sales_order.read', match: /^\/hop-dong\/\d+$/, hideIfEmpty: 'contracts' },
      {
        section: 'Phiếu xuất bán hàng – Hợp đồng',
        to: '/bao-gia-chot',
        label: 'Báo giá đã chốt (ERP)',
        icon: BadgeCheck,
        anyPerm: ['quote.read', 'control.check', 'purchase.read'],
      },
      // Soát phiếu giao hàng cuối ngày (P11 §11, anh Tiến 14/10/2026: bộ phận Kiểm soát làm)
      { section: 'Phiếu xuất bán hàng – Hợp đồng', to: '/soat-phieu', label: 'Soát phiếu hằng ngày', icon: ListChecks, perm: 'control.check' },
      { section: 'Phiếu xuất bán hàng – Hợp đồng', to: '/giu-hang', label: 'Giữ hàng', icon: PackageCheck, perm: 'stock.view_qty', hideIfEmpty: 'reservations' },
      { section: 'Phiếu xuất bán hàng – Hợp đồng', to: '/hoa-don', label: 'Xuất hóa đơn', icon: Receipt, soon: true, perm: 'sales_order.read' },
      // Việc riêng của bộ phận Kiểm soát
      // DEC-275: mã tạm TMP- cần tạo trên Asia để phiếu SO1 ghi được
      { section: 'Kiểm soát', to: '/kiem-soat', label: 'Kiểm soát đơn chốt', icon: ClipboardCheck, perm: 'control.check', end: true, match: /^\/kiem-soat\/\d+$/ },
      { section: 'Kiểm soát', to: '/kiem-soat/ma-tam', label: 'Mã tạm cần tạo trên Asia', icon: PackagePlus, perm: 'control.check' },
      { section: 'Kiểm soát', to: '/kiem-soat/thieu-hang', label: 'Thiếu hàng', icon: PackageX, perm: 'control.check' },
      // DEC-296: đơn mua ERP → đề nghị mua hàng Asia → phiếu mua hàng trong nước / nước ngoài
      { section: 'Kiểm soát', to: '/kiem-soat/mua-hang', label: 'Đồng bộ mua hàng', icon: ArrowRightLeft, perm: 'control.check' },
      { section: 'Kiểm soát', to: '/kiem-soat/nhat-ky', label: 'Nhật ký thao tác', icon: History, anyPerm: ['control.check', 'audit.read'] },
    ],
  },
  // Anh Tiến 28/10/2026 (DEC-580): "Đấu thầu" ra khỏi Bán hàng thành nhóm "Dự thầu"; /dau-thau chuyển hướng /du-thau
  {
    key: 'du-thau',
    label: 'Dự thầu',
    icon: Gavel,
    children: [
      { to: '/du-thau/san-thau', label: 'Săn thầu', icon: Radar, perm: 'tender.read' },
      { to: '/du-thau', label: 'Hồ sơ dự thầu', icon: FolderKanban, perm: 'tender.read', end: true, match: /^\/du-thau\/\d+$/ },
      { to: '/du-thau/bao-lanh', label: 'Bảo lãnh dự thầu', icon: ShieldHalf, perm: 'tender.read' },
      { to: '/du-thau/lich', label: 'Lịch mốc thầu', icon: CalendarClock, perm: 'tender.read' },
      { to: '/du-thau/tu-van', label: 'Tư vấn thầu cho CĐT', icon: BriefcaseBusiness, perm: 'tender.read', match: /^\/du-thau\/tu-van\/\d+$/ },
      { to: '/du-thau/phap-ly', label: 'Hành lang pháp lý', icon: Library, perm: 'tender.read' },
    ],
  },
  {
    key: 'crm',
    label: 'Khách hàng (CRM)',
    icon: Users,
    children: [
      { to: '/viec-can-lam', label: 'Việc cần làm', icon: ListTodo, perm: 'customer.read', badge: 'followups' },
      { to: '/lead', label: 'Lead', icon: UserPlus, perm: 'lead.read' },
      // DMS (DEC-610): tuyến viếng thăm, check-in GPS của NV thị trường
      { to: '/vieng-tham', label: 'Viếng thăm (DMS)', icon: MapPinCheck, perm: 'visit.use' },
      { to: '/khach-hang', label: 'Khách hàng', icon: Users, perm: 'customer.read' },
      // CR-016: mọi nhân viên (DEC-369) thấy dự án mình báo; KD thị trường thấy bản đồ mọi dự án
      { to: '/du-an', label: 'Dự án công trình', icon: HardHat, anyPerm: ['site.report', 'site.manage'], match: /^\/du-an\// },
      // CR-016 B4 (DEC-293): GĐ duyệt thưởng báo dự án, cài % hoa hồng CTV
      { to: '/thuong-ctv', label: 'Thưởng & hoa hồng CTV', icon: HandCoins, perm: 'ctv_reward.manage' },
      { to: '/pipeline', label: 'Cơ hội (Pipeline)', icon: Columns3, perm: 'opportunity.read', match: /^\/co-hoi\/\d+$/ },
      { to: '/khieu-nai', label: 'Khiếu nại', icon: MessageSquareWarning, perm: 'complaint.read' },
      { to: '/kpi-kinh-doanh', label: 'KPI kinh doanh', icon: Target, perm: 'crm.kpi.read' },
      // DEC-630: lead, doanh số theo kênh / chiến dịch quảng cáo
      { to: '/hieu-qua-quang-cao', label: 'Hiệu quả quảng cáo', icon: Megaphone, perm: 'crm.kpi.read_all' },
      { to: '/khach-hang/gan-ma-asia', label: 'Gắn mã Asia', icon: Link2, perm: 'customer.link_asia' },
    ],
  },
  {
    key: 'nhap-hang',
    label: 'Nhập hàng',
    icon: PackageOpen,
    children: [
      {
        to: '/de-nghi-mua',
        label: 'Đề nghị mua',
        icon: ClipboardList,
        anyPerm: ['purchase_request.create', 'purchase.read'],
        match: /^\/de-nghi-mua\/(\d+|moi)$/,
      },
      { to: '/mua-hang-nhanh', label: 'Mua hàng nhanh', icon: Zap, perm: 'purchase.manage' },
      { to: '/don-mua', label: 'Đơn mua', icon: FileInput, perm: 'purchase.read', match: /^\/don-mua\/(\d+|moi)$/ },
      { to: '/nha-cung-cap', label: 'Nhà cung cấp', icon: Building2, perm: 'supplier.read' },
      { to: '/bang-gia-ncc', label: 'Bảng giá NCC', icon: ReceiptText, perm: 'price.view_cost' },
      // DEC-448: tồn kho âm (tồn Asia + bóc SO3 ngoài khoảng ngày) → cần nhập, đề xuất kiểm kho
      { to: '/ton-kho-am', label: 'Tồn kho âm', icon: TrendingDown, anyPerm: ['purchase.read', 'stock_doc.read'] },
      { to: '/cong-no-ncc', label: 'Công nợ NCC', icon: Wallet, soon: true, perm: 'purchase.read' },
    ],
  },
  {
    // Xuất nhập khẩu (DEC-537): mỗi mục bỏ "soon" khi PR tính năng của mục đó merge
    key: 'xnk',
    label: 'Xuất nhập khẩu',
    icon: Ship,
    children: [
      { to: '/ke-hoach-nhap', label: 'Kế hoạch nhập khẩu', icon: CalendarClock, perm: 'trade.read' },
      { to: '/lo-hang', label: 'Lô hàng XNK', icon: Anchor, perm: 'trade.read' },
      { to: '/dam-phan', label: 'Đàm phán NCC', icon: Handshake, perm: 'trade.manage' },
      { to: '/ra-soat-hop-dong', label: 'Rà soát hợp đồng', icon: FileSearch, perm: 'trade.manage' },
      { to: '/kiem-hang', label: 'Kiểm hàng & khiếu nại', icon: ClipboardCheck, anyPerm: ['trade.quality', 'trade.manage'] },
      { to: '/ma-hs', label: 'Mã HS & phòng vệ TM', icon: Hash, perm: 'trade.read' },
      { to: '/ty-gia', label: 'Tỷ giá & thanh toán', icon: Coins, perm: 'trade.finance' },
      { to: '/cuoc-van-chuyen', label: 'Cước vận chuyển', icon: Ship, perm: 'trade.read' },
      { to: '/bao-cao-xnk', label: 'Báo cáo XNK', icon: ChartColumn, perm: 'trade.finance' },
    ],
  },
  {
    key: 'hang-hoa',
    label: 'Hàng hóa & kho',
    icon: Package,
    children: [
      { to: '/san-pham', label: 'Sản phẩm', icon: Package, perm: 'product.read' },
      { to: '/bang-gia', label: 'Bảng giá', icon: Tags, perm: 'price_list.read' },
      // Mọi người xem được (để biết máy dò mã hiểu thế nào); sửa cần product.dictionary
      { to: '/tu-dien-hang', label: 'Từ điển tên hàng', icon: BookText, perm: 'product.read' },
      // Mã gốc – Bước 1 (DEC-223): hãng chuẩn + gắn hãng cho mã Asia
      { to: '/hang-chuan', label: 'Hãng chuẩn', icon: BadgeCheck, perm: 'product.read' },
      // Mã gốc – Bước 2 (DEC-224): gom mã Asia khác hãng cùng quy cách
      { to: '/ma-goc', label: 'Mã gốc', icon: Layers, perm: 'product.read' },
      // P09 B3 (DEC-240): danh mục kho + thủ kho
      { to: '/kho', label: 'Danh mục kho', icon: Warehouse, perm: 'warehouse.read' },
      // P12 vỏ xe: thủ kho nhập vỏ mới, quét nhận vỏ cũ, thanh lý
      // P09 B4 (DEC-240): sổ kho ERP theo kho, phiếu nhập / xuất, soạn hàng cho thủ kho
      { to: '/ton-kho', label: 'Tồn kho theo kho', icon: Warehouse, perm: 'stock_doc.read' },
      { to: '/phieu-kho', label: 'Phiếu kho', icon: ClipboardList, perm: 'stock_doc.read', match: /^\/phieu-kho\/\d+$/ },
      { to: '/soan-hang', label: 'Soạn hàng', icon: PackageCheck, perm: 'delivery.pick' },
      // P09 B6: kiểm kho theo kho (đếm mù, Giám đốc duyệt → phiếu điều chỉnh)
      { to: '/kiem-kho', label: 'Kiểm kho', icon: ClipboardCheck, perm: 'stock_doc.read', match: /^\/kiem-kho\/\d+$/ },
      // DEC-350: nhóm vật tư theo kho, kiểm kho từng nhóm
      { to: '/nhom-vat-tu', label: 'Nhóm vật tư', icon: Layers, perm: 'stock_doc.read' },
      // DEC-353: vị trí hàng trong kho (tên | ảnh), tìm hàng nhanh khi thủ kho nghỉ
      { to: '/vi-tri-hang', label: 'Vị trí hàng', icon: MapPin, perm: 'stock_doc.read' },
    ],
  },
  // Anh Tiến 09/10/2026: nhóm Vận tải = Điều phối + Đội xe (P11, P12)
  {
    key: 'van-tai',
    label: 'Vận tải',
    icon: Truck,
    children: [
      // P11 B4: bảng điều phối theo ngày / xe + so phí vận chuyển báo giá với chi phí thực tế
      {
        section: 'Điều phối',
        to: '/van-chuyen',
        label: 'Điều phối vận chuyển',
        icon: Route,
        anyPerm: ['delivery.read_all', 'dispatch.plan', 'fleet.read', 'fleet.asia_reconcile'],
      },
      // DEC-412: NV tự đặt / quản lý xe Lalamove, TP / GĐ duyệt, quản trị tài khoản + ví Lalamove
      { section: 'Điều phối', to: '/xe-ngoai', label: 'Xe ngoài – Lalamove', icon: CarFront, anyPerm: ['carrier.self_book', 'carrier.approve', 'carrier.manage', 'carrier.wallet'] },
      { section: 'Điều phối', to: '/giao-hang', label: 'Giao hàng', icon: MapPinned, perm: 'delivery.read', match: /^\/giao-hang\/\d+$/ },
      // DEC-690: đơn SO3 thiếu địa chỉ giao / người nhận / SĐT – ngày nào xong ngày đó, so sánh tháng / quý / năm, theo NV
      { section: 'Điều phối', to: '/don-thieu-thong-tin-giao', label: 'Đơn SO3 thiếu thông tin giao', icon: ListChecks, anyPerm: ['delivery.read_all', 'control.check'] },
      { section: 'Điều phối', to: '/doi-xe?tab=chuyen-xe', label: 'Chuyến lấy hàng', icon: MapIcon, perm: 'fleet.read' },
      // PR5 4.4: lệnh giao sau 17h của NV kinh doanh, xác nhận cuối tháng
      { section: 'Điều phối', to: '/lenh-sau-17h', label: 'Lệnh giao sau 17h', icon: MoonStar, anyPerm: ['delivery.after17_confirm', 'fleet.trip_approve'] },
      { section: 'Đội xe', to: '/doi-xe', label: 'Xe & giấy tờ', icon: CarFront, perm: 'fleet.read' },
      { section: 'Đội xe', to: '/doi-xe?tab=tai-xe', label: 'Tài xế', icon: IdCard, perm: 'fleet.read' },
      // DEC-430: giao việc tài xế, nhắc việc khẩn, bảo dưỡng / rửa xe / giấy tờ tự động
      { section: 'Đội xe', to: '/doi-xe?tab=giao-viec', label: 'Giao việc tài xế', icon: BellRing, perm: 'fleet.read' },
      // DEC-614: phạt nguội – nhắc kiểm thứ Hai, mở csgt.vn từng xe, sổ vi phạm theo xe / tài xế
      { section: 'Đội xe', to: '/doi-xe?tab=phat-nguoi', label: 'Phạt nguội', icon: Siren, perm: 'fleet.read' },
      // PR5 4.1 + 4.8
      { section: 'Đội xe', to: '/doi-xe?tab=checklist', label: 'Checklist nhận xe', icon: ClipboardCheck, perm: 'fleet.read' },
      // DEC-670: xe về bãi ban đêm – mốc 17:30 / 21:00 / 22:00 / 24:00 / 04:00, tổng hợp ngày 15 + cuối tháng
      { section: 'Đội xe', to: '/doi-xe?tab=ve-bai', label: 'Xe về bãi', icon: Warehouse, perm: 'fleet.read' },
      { section: 'Đội xe', to: '/doi-xe?tab=quen-tra-xe', label: 'Quên trả xe', icon: AlarmClock, perm: 'fleet.read' },
      { section: 'Đội xe', to: '/doi-xe?tab=di-tinh', label: 'Đi tỉnh / qua đêm', icon: Route, perm: 'fleet.read' },
      { section: 'Đội xe', to: '/doi-xe?tab=don-km', label: 'Đơn & km tài xế', icon: BarChart3, perm: 'fleet.read' },
      { section: 'Đội xe', to: '/xe-giao-hang', label: 'Danh mục xe giao hàng', icon: Truck, perm: 'vehicle.manage' },
      { section: 'Đội xe', to: '/tai-xe', label: 'Tài xế: nhận xe, đổ dầu', icon: Fuel, perm: 'fleet.driver' },
      // Nhóm "Chi phí vận tải" (anh Tiến 19/10/2026): Xăng dầu chuyển từ Nhân sự sang, cùng Chi phí xe, Vỏ xe, Hiệu quả xe & tài xế
      // P12 (DEC-193): duyệt phiếu dầu có xác thực ảnh, báo cáo dầu, chốt kỳ; DEC-290: kế toán (fleet.fuel_pay) xem phiếu để chuyển tiền
      { section: 'Chi phí vận tải', to: '/xang-dau', label: 'Xăng dầu', icon: Droplets, anyPerm: ['fleet.read', 'fleet.fuel_pay'] },
      // P12 PR6 (§14): phiếu chi phí xe, báo cáo hiệu quả xe & tài xế + đối chiếu Asia
      { section: 'Chi phí vận tải', to: '/chi-phi-xe', label: 'Chi phí xe', icon: Wrench, anyPerm: ['fleet.read', 'fleet.manage'] },
      // P12 vỏ xe: sơ đồ vỏ theo xe, tất cả vỏ, kho vỏ, hiệu quả theo loại vỏ
      { section: 'Chi phí vận tải', to: '/vo-xe', label: 'Vỏ xe', icon: CircleDot, anyPerm: ['fleet.read', 'fleet.tire_stock'] },
      { section: 'Chi phí vận tải', to: '/hieu-qua-xe', label: 'Hiệu quả xe & tài xế', icon: Gauge, anyPerm: ['fleet.read', 'fleet.asia_reconcile'] },
    ],
  },
  // Anh Tiến 28/10/2026 (DEC-572): nhóm "Dự án thi công" = các tab của /thi-cong (Đội khoán cũ trong Nhân sự; /doi-khoan chuyển hướng).
  {
    key: 'thi-cong',
    label: 'Dự án SX-TC',
    icon: HardHat,
    children: [
      { to: '/thi-cong?tab=cong-trinh', label: 'Công trình', icon: Building2, anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      { to: '/thi-cong?tab=tien-do', label: 'Tiến độ công trình', icon: TrendingUp, anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      // DEC-573: bước giai đoạn tôi phụ trách, bước trễ / sắp đến hạn
      { to: '/thi-cong?tab=giai-doan', label: 'Giai đoạn', icon: ListTree, anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      { to: '/thi-cong?tab=ho-so-cn', label: 'Hồ sơ công nhân', icon: IdCard, anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      { to: '/thi-cong', label: 'Đợt thanh toán', icon: HandCoins, anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      { to: '/thi-cong?tab=hop-dong', label: 'Hợp đồng khoán', icon: FileSignature, anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      { to: '/thi-cong?tab=lai-lo', label: 'Lãi lỗ công trình', icon: Calculator, anyPerm: ['hr.piece.manage', 'hr.piece.approve'] },
      { to: '/thi-cong?tab=doi', label: 'Đội khoán', icon: Hammer, perm: 'hr.piece.manage' },
      { to: '/thi-cong?tab=thue', label: 'Thuế khấu trừ', icon: Receipt, perm: 'hr.piece.manage' },
    ],
  },
  // P21: mọi nghiệp vụ nhân sự chung một nhóm (Hồ sơ NV, Chấm công, Tính lương, Đơn từ); Xăng dầu ở Vận tải → Chi phí vận tải (19/10/2026)
  {
    key: 'nhan-su',
    label: 'Nhân sự',
    icon: Users2,
    children: [
      // P21 B1: hồ sơ nhân viên (Trưởng phòng chỉ thấy phòng mình)
      { to: '/nhan-su', label: 'Hồ sơ nhân viên', icon: IdCard, perm: 'hr.employee.read', end: true },
      // Anh Tiến 31/10/2026 "đã có trong tab nhân viên rồi, để ngoài làm gì": 3 mục Thử việc / Lao động / Ký trên app gộp 1 mục,
      // 3 tab giữ quyền menu riêng (menu.thu_viec, menu.hop_dong_lao_dong, menu.ky_hop_dong_lao_dong). Link cũ tự chuyển.
      { to: '/hop-dong-nhan-su', label: 'Hợp đồng nhân sự', icon: FileSignature, anyPerm: ['hr.contract.manage', 'hr.contract.sign'] },
      // Phép năm theo công tháng (DEC-244): số dư từng NV, nhập số phép đầu kỳ
      { to: '/phep-nam', label: 'Phép năm', icon: CalendarDays, perm: 'hr.attendance.lock' },
      // P21 B4a: mọi người tự chấm; tổ trưởng chấm tổ; văn phòng xem bảng công
      { to: '/cham-cong', label: 'Chấm công', icon: Clock, staffOnly: true },
      // P21 B6: GĐ, Kế toán
      {
        to: '/tinh-luong',
        label: 'Tính lương',
        icon: Calculator,
        anyPerm: ['hr.payroll.run', 'hr.payroll.approve', 'hr.salary.read', 'hr.payroll.bonus', 'hr.payout.manage', 'hr.payout.approve'],
      },
      // P21 PR 4: Cổng nhân viên (mọi người), duyệt đơn (TP/GĐ/Kế toán), nội quy (HCNS)
      { to: '/cong-nhan-vien', label: 'Cổng nhân viên', icon: UserRound, staffOnly: true },
      { to: '/duyet-don', label: 'Duyệt đơn', icon: ClipboardCheck, anyPerm: ['hr.leave.approve', 'hr.advance.check', 'hr.advance.approve'] },
      // DEC-249: Giao việc (tăng ca, Chủ nhật, điều động, việc thường) chuyển lên nhóm Tổng quan (DEC-323)
      // Đội khoán (P21 PR 10) chuyển sang nhóm "Dự án thi công" (DEC-572)
      { to: '/dao-tao', label: 'Đào tạo', icon: GraduationCap, perm: 'hr.training.manage' },
      // P21 PR 11b (DEC-314): HR mở / khóa kỳ, TP chấm phòng mình, GĐ duyệt tăng lương
      { to: '/kpi-nhan-su', label: 'KPI & tăng lương', icon: Target, anyPerm: ['hr.kpi.manage', 'hr.kpi.review', 'hr.kpi.raise_approve'] },
    ],
  },
  // Anh Tiến 25/10/2026 (DEC-486): mọi tài liệu chung một nhóm. Quy chuẩn - Luật = "Tài liệu công ty" P24 cũ (/tai-lieu, /noi-quy chuyển hướng),
  // Tài liệu hàng hóa = "CO/CQ hàng hóa" cũ. Hợp đồng, Hình ảnh, Ghi âm – video thêm ở PR sau
  // (mục chưa có dữ liệu thì ẩn, anh Tiến 20/10/2026). Quy chế: người soạn thấy kho tài liệu nội bộ, NV thấy văn bản áp dụng cho mình.
  {
    key: 'tai-lieu-dn',
    label: 'Tài liệu doanh nghiệp',
    icon: LibraryBig,
    children: [
      { to: '/quy-che', label: 'Quy chế doanh nghiệp', icon: BookMarked, staffOnly: true },
      // Anh Tiến 25/10/2026: thêm Sổ tay vận hành, Tài liệu an toàn (lọc theo loại trong kho tài liệu nội bộ)
      { to: '/so-tay-van-hanh', label: 'Sổ tay vận hành', icon: BookOpenCheck, staffOnly: true },
      { to: '/tai-lieu-an-toan', label: 'Tài liệu an toàn', icon: ShieldAlert, staffOnly: true },
      { to: '/tai-lieu', label: 'Quy chuẩn - Luật', icon: Landmark, anyPerm: ['hr.policy.manage', 'docs.legal.read'] },
      { to: '/co-cq', label: 'Tài liệu hàng hóa', icon: FileCheck2, perm: 'docs.cert.read' },
      // DEC-491: kho ảnh sản phẩm / thi công và ghi âm, video (kịch bản chăm sóc, ghi âm công nợ)
      { to: '/hinh-anh', label: 'Hình ảnh', icon: ImagePlay, perm: 'media.read' },
      { to: '/ghi-am-video', label: 'File ghi âm, video', icon: FileAudio, perm: 'media.read' },
      // DEC-497: ảnh quảng cáo, bài Zalo, video NV làm ở Trợ lý bán hàng AI (nháp video mở lại làm tiếp)
      { to: '/thu-vien-ban-hang', label: 'Thư viện bán hàng', icon: BookImage, perm: 'poster.use' },
      // DEC-492: nơi lưu hợp đồng đã ký (ký số / ký tay); HĐ cá nhân chỉ người quản lý HĐLĐ
      { to: '/hop-dong-doanh-nghiep', label: 'Hợp đồng doanh nghiệp', icon: FileSignature, perm: 'contract_doc.read' },
      { to: '/hop-dong-ca-nhan', label: 'Hợp đồng cá nhân', icon: UserRoundPen, perm: 'hr.contract.manage' },
    ],
  },
  {
    key: 'ai',
    label: 'AI & Marketing',
    icon: Bot,
    children: [
      { to: '/tro-ly-ban-hang', label: 'Trợ lý bán hàng AI', icon: Sparkles, perm: 'poster.use' },
      { to: '/tro-ly-ai', label: 'Trợ lý AI', icon: Bot, perm: 'ai.use' },
      { to: '/tim-anh', label: 'Tìm ảnh sản phẩm', icon: Images, perm: 'ai.use' },
      { to: '/poster', label: 'Poster', icon: Megaphone, perm: 'poster.use', match: /^\/poster\// },
      // DEC-640 (anh Tiến 31/10/2026): NV được chọn đăng bài (AI viết) và ảnh hàng xuất / giao hàng lên website
      { to: '/dang-bai-web', label: 'Đăng bài và Ảnh', icon: Globe, anyPerm: ['web_post.write', 'web_post.approve'] },
    ],
  },
  {
    key: 'bao-cao',
    label: 'Báo cáo',
    icon: BarChart3,
    children: [
      { to: '/bao-cao', label: 'Báo cáo bán hàng', icon: BarChart3, soon: true, end: true, perm: 'quote.read' },
      // P06 B8 (DEC-131 A): chỉ admin
      { to: '/gia-cao', label: 'Giá cao so mặt bằng', icon: TrendingUp, perm: 'price.view_market_flag' },
    ],
  },
  // Hướng dẫn sử dụng theo vai trò (mọi người dùng)
  { to: '/huong-dan', label: 'Hướng dẫn sử dụng', icon: BookOpen },
  {
    key: 'cai-dat',
    label: 'Cài đặt',
    icon: Settings,
    children: [
      { to: '/nhan-vien', label: 'Nhân viên & phân quyền', icon: UserCog, perm: 'user.manage' },
      { to: '/dieu-khoan', label: 'Điều khoản TT / GH', icon: ScrollText, perm: 'terms.manage' },
      // P23: GĐ sửa quy trình duyệt dùng chung
      { to: '/cai-dat/quy-trinh-duyet', label: 'Quy trình duyệt', icon: Workflow, perm: 'workflow.configure' },
      // P23 PR 2: GĐ cấp / thu hồi quyền ký cho công ty (giấy ủy quyền)
      { to: '/cai-dat/quyen-ky', label: 'Quyền ký', icon: KeyRound, perm: 'esign.authority' },
      // NavLeaf.perm chỉ nhận 1 quyền: dùng customer.import; trang tự lọc loại file theo quyền (product.import, supplier.manage)
      // Anh Tiến 08/10/2026: tình trạng đồng bộ hai chiều Asia ↔ ERP
      { to: '/dong-bo-asia', label: 'Đồng bộ Asia', icon: RefreshCw, perm: 'asia.sync' },
      {
        to: '/nhap-file',
        label: 'Nhập file Excel',
        icon: Upload,
        anyPerm: ['customer.import', 'product.import', 'supplier.manage', 'product.translate', 'hr.employee.manage'],
      },
      { to: '/su-kien-loi', label: 'Sự kiện lỗi', icon: TriangleAlert, perm: 'notification.admin' },
      { to: '/nhat-ky-ai', label: 'Nhật ký AI', icon: Activity, perm: 'ai.admin' },
      { to: '/ai-router', label: 'AI Router', icon: Route, perm: 'ai.admin' },
      // DEC-531: GĐ dán khóa giọng đọc Việt (Azure, FPT) trên web
      { to: '/cai-dat/giong-doc', label: 'Giọng đọc', icon: AudioLines, perm: 'ai.voice_keys' },
      // DEC-593: website (plugin WordPress) lấy sản phẩm bán chạy, gửi yêu cầu báo giá vào ERP
      { to: '/cai-dat/ket-noi-website', label: 'Kết nối website', icon: Globe, perm: 'web_site.manage' },
      // DEC-691: nối Google Ads / GA4 / Facebook, kéo chi phí quảng cáo hằng ngày
      { to: '/cai-dat/tai-khoan-quang-cao', label: 'Tài khoản quảng cáo', icon: Megaphone, perm: 'ads.manage' },
    ],
  },
];

/** Các trang chưa có chức năng: tiêu đề + mô tả hiển thị ở trang "đang phát triển". */
export const SOON_PAGES: Record<string, { title: string; desc: string; icon: LucideIcon }> = {
  '/hoa-don': { title: 'Xuất hóa đơn', icon: Receipt, desc: 'Lập và gửi hóa đơn điện tử cho đơn hàng đã giao.' },
  '/cong-no-ncc': { title: 'Công nợ nhà cung cấp', icon: Wallet, desc: 'Theo dõi công nợ phải trả, lịch thanh toán.' },
  '/bao-cao': { title: 'Báo cáo', icon: BarChart3, desc: 'Doanh số theo nhân viên, khách hàng, nhóm hàng; tỷ lệ chốt báo giá.' },
};
